<script>
import RexModalV3 from '@/rex-components/RexModalV3.vue';
import RexStepper from '@/rex-components/rex-stepper.vue';
import LogWidgetSavedSearchPage from '@/components/Log/stepper-pages/LogWidgetSavedSearchPage.vue';
import LogWidgetAddDashboardPage from '@/components/Log/stepper-pages/LogWidgetAddDashboardPage.vue';
import RexButtonV3 from '@/rex-components/RexButtonV3.vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import { addWidgetStepperLabels } from '@/components/Log/log.types';
import { mapGetters, mapMutations } from 'vuex';
import { NAME_FIELD_REGEX } from '@/components/Log/log.constants';

export default {
  emits: ['onSubmit', 'onClose'],
  components: {
    RexModalV3,
    RexButtonV3,
    RexStepper,
    LogWidgetSavedSearchPage,
    LogWidgetAddDashboardPage,
    RexInfoLabel,
  },
  props: {
    initValues: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    formErrMsg: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('logState/logCommonState', [
      'getWidgetFormData',
      'getWidgetSavedSearchInfo',
      'getWidgetAddDashboardInfo',
    ]),
    getSubmitLabel() {
      return this.currentStage === 1
        ? this.$t('log.next')
        : this.$t('log.create');
    },
    getDisableSubmitBtn() {
      return (
        !this.getWidgetFormData.isQueryValid ||
        !this.getWidgetFormData.isSavedSearchValid ||
        this.isLoading
      );
    },
    stepperLabels() {
      return addWidgetStepperLabels();
    },
  },
  data() {
    return {
      currentStage: 1,
    };
  },
  methods: {
    ...mapMutations('logState/logCommonState', [
      'resetAddWidgetData',
      'setWidgetFormData',
    ]),
    checkInvalidName(nameStr, strLength) {
      const regex = new RegExp(NAME_FIELD_REGEX);

      return (
        !regex.test(nameStr) || !nameStr?.trim() || nameStr?.length > strLength
      );
    },
    validateSavedSearchPage() {
      let isSavedSearchInvalid = this.checkInvalidName(
        this.getWidgetSavedSearchInfo.savedSearchName,
        50,
      );

      this.setWidgetFormData({
        field: 'isSavedSearchValid',
        value: !isSavedSearchInvalid,
      });

      return isSavedSearchInvalid;
    },
    validateAddDashboardPage() {
      const isWidgetNameInvalid = this.checkInvalidName(
        this.getWidgetAddDashboardInfo.widgetName,
        25,
      );
      this.setWidgetFormData({
        field: 'isWidgetNameValid',
        value: !isWidgetNameInvalid,
      });

      const isDashNameInvalid = this.getWidgetFormData.isNewDash
        ? this.checkInvalidName(
            this.getWidgetAddDashboardInfo.dashboardName,
            20,
          )
        : false;
      this.setWidgetFormData({
        field: 'isDashNameValid',
        value: !isDashNameInvalid,
      });

      const isDashboardInvalid =
        !this.getWidgetFormData.isNewDash &&
        (!this.getWidgetAddDashboardInfo.dashboard ||
          !this.getWidgetAddDashboardInfo.dashboard.id);

      this.setWidgetFormData({
        field: 'isDashboardValid',
        value: !isDashboardInvalid,
      });

      return isWidgetNameInvalid || isDashNameInvalid || isDashboardInvalid;
    },
    onPrimaryBtn() {
      if (this.currentStage === 1) {
        if (this.validateSavedSearchPage()) return;
        this.currentStage = 2;
      } else {
        if (this.validateAddDashboardPage()) return;
        this.$emit('onSubmit');
      }
    },
    onClearBtn() {
      if (this.currentStage === 1) {
        this.resetAddWidgetData('savedSearchInfo');
      } else {
        this.resetAddWidgetData('addDashboardInfo');
      }
    },
  },
};
</script>

<template>
  <div>
    <RexModalV3
      :show="true"
      :title="$t('log.addWidget')"
      @onClose="() => $emit('onClose')"
      width="40.25rem"
      :enableFooter="true"
    >
      <template #body>
        <div class="add-widget-form-body">
          <div class="widget-stepper-box">
            <RexStepper
              :stepLabels="stepperLabels"
              :currentStage="currentStage"
              stepperType="Completion"
              :labelStyle="{ width: '16.5rem' }"
            />
          </div>
          <div class="add-widget-steps-form">
            <LogWidgetSavedSearchPage v-if="currentStage === 1" />
            <LogWidgetAddDashboardPage v-if="currentStage === 2" />
          </div>
          <RexInfoLabel
            v-if="!!formErrMsg"
            :isError="true"
            :info="formErrMsg"
          />
        </div>
      </template>
      <template #footer>
        <div class="footer-container">
          <div>
            <RexButtonV3
              variant="default"
              id="log-add-widget-form-cancel"
              @onClick="$emit('onClose')"
            >
              {{ $t('log.cancel') }}
            </RexButtonV3>
          </div>
          <div>
            <RexButtonV3
              variant="default"
              id="log-add-widget-form-clear"
              @onClick="onClearBtn"
            >
              {{ $t('log.clear') }}
            </RexButtonV3>
            <RexButtonV3
              :disabled="getDisableSubmitBtn"
              id="log-add-widget-form-submit"
              @onClick="onPrimaryBtn"
              :loading="isLoading"
            >
              {{ getSubmitLabel }}
            </RexButtonV3>
          </div>
        </div>
      </template>
    </RexModalV3>
  </div>
</template>

<style lang="scss" scoped>
.widget-stepper-box {
  @include flex-center-justify;
  width: 100%;
  background-color: $bg-secondary;
  padding-bottom: 2.625rem;
  padding-top: 3.563rem;
}
.add-widget-form-body {
  width: 36rem;
}
.add-widget-steps-form {
  margin-top: 1.75rem;
  height: 32rem;
}
.footer-container {
  @include flex-center-justify(space-between);
}
.cancel-btn {
  @include dropshadow-effect($opacity: 0);
  border: none;
  padding: 0;
  margin-right: 1.75rem;
  font-size: 1rem;
}
.clear-btn {
  margin-right: 1rem;
  font-size: 1rem;
}
.submit-btn {
  font-size: 1rem;
  padding: 0 1rem 0 1rem;
}
</style>
